import { Translation } from '@modeso/aumico-lib-core-fe';
import { ITranslationEntry, ITranslations } from '@modeso/types__aumico-lib-annualreport-be';
import { INotesTypeEntityDTO } from '@modeso/types__aumico-lib-notes-be';

/** An alias, not an interface: only an alias gets the index signature `Record<string, unknown>` needs. */
export type IReportTranslateInput = {
    reportLanguage: string;
    targetLanguage: string;
    toTranslate: string[];
    examples: Record<string, string>[];
};

/** A block entry as the visitors insert it: `fromMasterData` marks a mapping translation merged in by
 * `withMasterData`, so the review can tell it (grey) from the AI (blue). Never saved. */
export type IInsertedTranslationEntry = ITranslationEntry & { fromMasterData?: true };

export type IReportTranslateOutput = {
    pairs?: Record<string, string>[];
};

export class TranslationJobHelper {
    /** Identifiers stay out: whatever travels is stored unencrypted by the AI service. */
    static buildJobPayload(args: {
        reportLanguage: string;
        targetLanguage: string;
        gaps: Set<string>;
        block: ITranslations | null | undefined;
        translatableItems: Map<string, Translation>;
        notesTypes: INotesTypeEntityDTO[];
    }): IReportTranslateInput {
        const { reportLanguage, targetLanguage, gaps, block, translatableItems, notesTypes } = args;
        const examples = new Map<string, string>();

        const addExample = (source: unknown, target: unknown): void => {
            if (typeof source !== 'string' || typeof target !== 'string') {
                return;
            }
            if (source === '' || target === '' || source === target || gaps.has(source)) {
                return;
            }
            if (!examples.has(source)) {
                examples.set(source, target);
            }
        };

        Object.entries(block?.[targetLanguage]?.entries ?? {}).forEach(([source, entry]: [string, ITranslationEntry]) =>
            addExample(source, entry?.value),
        );

        translatableItems.forEach((translation) =>
            addExample(translation?.[reportLanguage], translation?.[targetLanguage]),
        );

        notesTypes.forEach((notesType) =>
            addExample(notesType?.title?.[reportLanguage], notesType?.title?.[targetLanguage]),
        );

        return {
            reportLanguage,
            targetLanguage,
            toTranslate: Array.from(gaps),
            examples: Array.from(examples, ([source, target]) => ({
                [reportLanguage]: source,
                [targetLanguage]: target,
            })),
        };
    }

    /** Existing entries win, so a rerun never overwrites a hand correction; the model also echoes
     * examples back, hence the check against what was asked for. */
    static mergeIntoBlock(args: {
        block: ITranslations | null | undefined;
        output: IReportTranslateOutput | null | undefined;
        reportLanguage: string;
        targetLanguage: string;
        requested: Set<string>;
    }): { translations: ITranslations; added: number } {
        const { block, output, reportLanguage, targetLanguage, requested } = args;
        const bucket = block?.[targetLanguage];
        const entries: Record<string, ITranslationEntry> = { ...(bucket?.entries ?? {}) };
        let added = 0;

        (output?.pairs ?? []).forEach((pair) => {
            const source = pair?.[reportLanguage];
            const value = pair?.[targetLanguage];
            if (typeof source !== 'string' || typeof value !== 'string' || value === '') {
                return;
            }
            if (!requested.has(source) || entries[source] != null) {
                return;
            }
            entries[source] = { value };
            added++;
        });

        return {
            translations: { ...(block ?? {}), [targetLanguage]: { ...(bucket ?? {}), entries } },
            added,
        };
    }

    /**
     * The block entries as the inserting code should see them: a master-data translation of the same
     * source text beats the AI entry, so a text the mapping already knows is never a gap and reads the
     * same everywhere. Hand corrections still win. First mapping translation per source text wins.
     * For inserting only — never saved, and the marking reads the real block.
     */
    static withMasterData(
        entries: Record<string, ITranslationEntry> | undefined,
        translatableItems: Map<string, Translation>,
        reportLanguage: string,
        targetLanguage: string,
    ): Record<string, IInsertedTranslationEntry> {
        const merged: Record<string, IInsertedTranslationEntry> = { ...(entries ?? {}) };
        const taken = new Set<string>();
        translatableItems.forEach((translation) => {
            const source = translation?.[reportLanguage];
            const target = translation?.[targetLanguage];
            if (typeof source !== 'string' || typeof target !== 'string' || source === '' || target === '') {
                return;
            }
            if (taken.has(source) || merged[source]?.hasUserEdited === true) {
                return;
            }
            taken.add(source);
            merged[source] = { value: target, fromMasterData: true };
        });

        return merged;
    }
}
